import * as React from "react"

import {
  computeLayout,
  layoutsEqual,
  type MeasuredToolbarItem,
} from "./computeLayout"
import type { ToolbarItem, ToolbarLayout } from "./types"

export interface ToolbarMeasurements {
  items: MeasuredToolbarItem[]
  gap: number
  triggerWidth: number
}

function allFullLayout(items: ToolbarItem[]): ToolbarLayout {
  return {
    visible: items.map((item) => ({ id: item.id, mode: "full" as const })),
    overflow: [],
  }
}

/**
 * Tracks the toolbar container's width and recomputes which items are full,
 * compact, or overflowed whenever it changes.
 *
 * `getMeasurements` reads the hidden measurement layer; it is invoked inside a
 * `requestAnimationFrame` (the Sofia panel drag fires resize events rapidly) and
 * in a layout effect so the first paint is already correct rather than flashing
 * the full layout. `signature` should change whenever the item set or any
 * measured property (label, icon, disabled) changes, to force a re-measure.
 */
export function useToolbarOverflow(
  items: ToolbarItem[],
  getMeasurements: () => ToolbarMeasurements | null,
  signature: string,
): {
  containerRef: React.RefObject<HTMLDivElement | null>
  layout: ToolbarLayout
} {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [layout, setLayout] = React.useState<ToolbarLayout>(() =>
    allFullLayout(items),
  )

  const recompute = React.useCallback(() => {
    const element = containerRef.current
    if (!element) {
      return
    }
    const available = element.clientWidth
    if (available <= 0) {
      return
    }
    const measurements = getMeasurements()
    if (!measurements) {
      return
    }
    const next = computeLayout(
      measurements.items,
      available,
      measurements.gap,
      measurements.triggerWidth,
    )
    setLayout((prev) => (layoutsEqual(prev, next) ? prev : next))
  }, [getMeasurements])

  React.useLayoutEffect(() => {
    recompute()
  }, [signature, recompute])

  React.useEffect(() => {
    const element = containerRef.current
    if (!element || typeof ResizeObserver === "undefined") {
      return
    }
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(recompute)
    })
    observer.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [recompute])

  return { containerRef, layout }
}
